import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 grid-lines">
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background" />
      <Link to="/" className="absolute top-8 left-8 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-volt transition-colors duration-200">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md bg-card border border-border p-8 md:p-10">
        <div className="font-display font-black text-2xl tracking-tighter uppercase">Etoire<span className="text-volt">.</span></div>
        <h1 className="font-display font-bold text-xl uppercase tracking-tight mt-6">Admin Access</h1>
        <p className="text-sm text-muted-foreground mt-2">Sign in to manage the studio.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Email or Username</label>
            <input
              data-testid="login-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full mt-2 bg-background border border-border focus:border-volt outline-none px-4 py-3 text-sm transition-colors duration-200"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Password</label>
            <input
              data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full mt-2 bg-background border border-border focus:border-volt outline-none px-4 py-3 text-sm transition-colors duration-200"
            />
          </div>
          {error && <p data-testid="login-error" className="text-destructive text-sm font-mono">{error}</p>}
          <button
            data-testid="login-submit" type="submit" disabled={loading}
            className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-4 hover:brightness-110 disabled:opacity-50 transition-all duration-200">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
